import {
  convertNumberToSplitStation,
  getUUID,
  isNullOrEmpty,
} from "../../../_utils/generalUtils";
import "../../../_typedefs/adrV3TypeDefs";
import { SUBLOCATION_WORKPERFORMED_TYPE } from "../constants/adrConstantsV3";

/**
 * Collection of helper utility functions for ADR.
 * @copyright 2022, California Department of Transportation
 */

export const MAX_COLUMNS = 8;

export function cascadeFilter(list, path, filterValue) {
  if (Array.isArray(list))
    return filterValue ? list.filter((i) => i[path] === filterValue) : list;
  return new Error("ERROR: cascadeFilter() did not receive an array.");
}

export const getLaborOption = (labor, trade = null) => ({
  value: labor.laborId,
  label: trade ? `${labor.fullName} (${trade})` : labor.fullName,
});

export const getEquipOption = (equip) => ({
  value: equip.equipmentId,
  label: equip.numberMakeDesc,
});

/**
 * Same as getEquipOption, plus the raw Class/Make/Model/Number/Description/contractorId fields
 * needed to render the ADR equipment dropdown's custom option/closed-control (getEquipCodeLine +
 * muted description). Shared by AdrEquipmentSetV3's project-wide fallback list and
 * AdrEquipmentSetRowV3's per-contractor catalog list so both stay in the same shape.
 * @param {Object} equip - normalized equipment/ProjectEquipment client object (toEquipState() shape)
 */
export const getEquipDropdownOption = (equip) => ({
  ...getEquipOption(equip),
  contractorId: equip.contractorId,
  equipClass: equip.equipClass,
  make: equip.make,
  model: equip.model,
  number: equip.number,
  description: equip.description,
});

/**
 * Sorts a list of options (or anything with a `.label`) alphabetically, case-insensitively.
 * Returns a new array — does not mutate the input.
 * @param {Array<{label: string}>} options
 */
export const sortOptionsByLabel = (options) =>
  [...options].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

export const getContractorOption = (contractor) => ({
  value: contractor.contractorId,
  label: contractor.isPrime ? contractor.name + " (P)" : contractor.name,
});

/**
 * "(Number) Class / Make / Model Code" from a normalized equipment/ProjectEquipment client object
 * (equipClass, make, model, number — the toEquipState() shape). Shared by the ADR equipment
 * dropdown and the Review-mode equipment table so both stay in the same format.
 * @param {Object} equip
 * @returns {string}
 */
export const getEquipCodeLine = (equip) => {
  if (!equip) return "(No. N/A)";
  const classMakeModel = [equip.equipClass, equip.make, equip.model].filter(Boolean).join(" / ");
  const numberPart = equip.number ? `(${equip.number})` : "(No. N/A)";
  return [numberPart, classMakeModel].filter(Boolean).join(" ");
};

/**
 * "Prime" / "Sub" / "Supplier" for a projContractorList item (isPrime/isSupplier flags).
 * @param {Object} contractor
 * @returns {string}
 */
export const getContractorRole = (contractor) =>
  contractor.isPrime ? "Prime" : contractor.isSupplier ? "Supplier" : "Sub";

/**
 * Color for a react-select menu option's secondary/muted line (e.g. equipment Description,
 * contractor role), given the option's highlight state. react-select's pale-blue hover
 * background (isFocused) and solid-blue "currently selected" background (isSelected) both
 * wash out Bootstrap's .text-muted gray (which also carries !important, so an inline style
 * can't simply override it while the class is still applied) — this picks an explicit color
 * for those two states instead, leaving the default (undefined) to fall back to .text-muted.
 * @param {boolean} isFocused
 * @param {boolean} isSelected
 * @returns {string|undefined}
 */
export const getMenuOptionMutedColor = (isFocused, isSelected) =>
  isSelected ? "rgba(255, 255, 255, 0.85)" : isFocused ? "#3d4f61" : undefined;

export const getKeywordOption = (keyword) => ({
  value: keyword.keywordId,
  label: keyword.keywordTag,
});

export const getLocationOption = (location, order = null) => ({
  value: location ? location.locationId : getUUID(),
  label: location ? location.name : "(N/A)",
  isGeneric: location ? location.isGeneric : false,
  order,
});

/**
 * Returns the react-select value for special subLocWp.
 * @param {number} subLocType - a SUBLOCATION_WORKPERFORMED_TYPE value
 * @returns {number} a negative value
 */
export const getSpecialLocationOptionValue = (subLocType) => {
  if (subLocType == null) return;
  return 0 - subLocType;
};

export const getSublocationOption = (sublocation) => ({
  value: sublocation.sublocationId,
  label: sublocation.name,
});

// TODO: modify this with getters directly inside Wp
export const wpIsItem = (wp) => wp.itemId && !wp.ccoId && !wp.pendingIssueId;

export const wpIsCco = (wp) => !wp.itemId && wp.ccoId && !wp.pendingIssueId;

export const wpIsPendingIssue = (wp) => !wp.itemId && !wp.ccoId && wp.pendingIssueId;

export const getSectionSequenceId = (section, index) => section + "_" + index;

/**
 * Returns new locWpr
 * @param {number} wpTabId
 * @returns {import("../../../_typedefs/adrV3TypeDefs").LocationWorkPerformedClient}
 */
export const getNewLocWpr = () => ({
  locationId: 0,
  workPerformedId: 0,
  workDescription: "",
  workDescriptionDraftState: "",
  inspectionType: 0,
  quantity: 0,
  isControllingOperation: false,
  isDisputed: false,
  isSublocation: false,
  sublocationId: 0,
  sublocationWorkPerformedId: 0,
  sublocationWorkPerformedType: SUBLOCATION_WORKPERFORMED_TYPE.NESTEDLOCATION,
  stationFromOrAt: "",
  stationFromOrAtOffsetValue: "",
  stationFromOrAtOffsetDirection: "",
  stationTo: "",
  stationToOffsetValue: "",
  stationToOffsetDirection: "",
  miscMaterials: [],
  testsPerformed: [],
  attachments: [],
  nestedSublocations: [],
});

/**
 * Returns the location name string.
 *
 * @param {number} locId - locationId
 * @param {Object}
 */
export const getLocationName = (locId, projLocationList) => {
  const loc = (projLocationList || []).find((l) => l.locationId === locId);
  if (loc) return loc.name;
  return "(N/A)";
};

/**
 * Get the line name and sta. ranges
 * @param {import("../../../_typedefs/adrV3TypeDefs").LocationWorkPerformedClient} locWp
 * @param {Object[]} projLocationList
 * @returns
 */
export const getLineStaLabel = (locWp, projLocationList) => {
  const line = (projLocationList || []).find((l) => l.locationId === locWp.sublocationId);
  const staFromOrAt = locWp.stationFromOrAt
    ? convertNumberToSplitStation(locWp.stationFromOrAt)
    : null;
  const staFromOrAtOffset =
    locWp.stationFromOrAtOffsetValue > 0 &&
    locWp.stationFromOrAtOffsetDirection &&
    `, ${locWp.stationFromOrAtOffsetValue}' ${locWp.stationFromOrAtOffsetDirection}`;
  const staTo = locWp.stationTo ? convertNumberToSplitStation(locWp.stationTo) : null;
  const staToOffset =
    locWp.stationToOffsetValue > 0 &&
    locWp.stationToOffsetDirection &&
    `, ${locWp.stationToOffsetValue}' ${locWp.stationToOffsetDirection}`;
  const sta = staFromOrAt
    ? staTo
      ? `, from ${staFromOrAt.fullSta}+${staFromOrAt.partialSta}${
          staFromOrAtOffset || ""
        } to ${staTo.fullSta}+${staTo.partialSta}${staToOffset || ""}`
      : `, ${staFromOrAt.fullSta}+${staFromOrAt.partialSta}${staFromOrAtOffset || ""}`
    : "";
  return line ? `"${line.name}" Line${sta}` : "(N/A)";
};

/**
 * Returns the inspection type.
 * Note: inspection types are NOT stored in database - they're
 * represented by an enum.
 *
 * @param {number} inspTypeId
 * @returns {string}
 */
export const getInspectionType = (inspTypeId) =>
  ["None", "Benchmark", "Intermittent", "Continuous"][+inspTypeId] || "";

/**
 * Returns the misc. mat'l unit of measure (string).
 *
 * @param {number} lmmUnitId - locationMiscMaterialsId
 * @param {Object[]} miscMaterialsUnitsOfMeasure
 */
export const getMiscMaterialsUnitOfMeasure = (lmmUnitId, miscMaterialsUnitsOfMeasure) => {
  let unit = "";
  if (lmmUnitId) {
    const mmu = (miscMaterialsUnitsOfMeasure || []).find(
      (mmu) => mmu.miscMaterialsUnitOfMeasureId === lmmUnitId
    );
    if (mmu) unit = mmu.unitOfMeasure;
  }
  return unit;
};

/**
 * Returns the test type string.
 *
 * @param {number} ltpId - locationTestPerformedId
 * @param {Object[]} fieldTestTypes
 * @returns {string}
 */
export const getTestType = (ltpId, fieldTestTypes) => {
  let testType = "";
  if (ltpId !== 0 && ltpId !== undefined) {
    const test = (fieldTestTypes || []).find((tt) => tt.fieldTestTypeId === ltpId);
    testType = test.fieldTestCode + ", " + test.fieldTestDescription;
  }
  return testType;
};

/**
 * Returns item, CCO, or pending issue number.
 * Used by PDF preview to map WP columns and Desc. of Work.
 *
 * @param {Object} wp - the full workPerformed object
 * @returns {string} The number converted to string
 */
export const getWpNumber = (wp, projItemList, projCcoList, projPendingIssueList) => {
  if (!wp) return "-";
  if (wpIsItem(wp)) {
    const item = (projItemList || []).find((i) => i.bidItemId === wp.itemId);
    return item ? item.itemNo : "-";
  }
  if (wpIsCco(wp)) {
    const cco = (projCcoList || []).find((c) => c.ccoId === wp.ccoId);
    return cco ? cco.ccoNo : "-";
  }
  if (wpIsPendingIssue(wp)) {
    const pi = (projPendingIssueList || []).find(
      (pi) => pi.pendingIssueId === wp.pendingIssueId
    );
    return pi ? pi.pendingIssueNo : "-";
  }
  return "-";
};

export const getWpPrefix = (wp) => {
  if (wpIsItem(wp)) return "BI#";
  if (wpIsCco(wp)) return "CO#";
  if (wpIsPendingIssue(wp)) return "PI#";
};

/**
 * Returns the full caption for the provided workPerformed.
 *
 * @param {Object} wp - the full workPerformed object.
 * @returns {string} Empty string if the full caption cannot be obtained.
 */
export const getWpCaption = (wp, projItemList, projCcoList, projPendingIssueList) => {
  if (!wp) return "";
  if (wpIsItem(wp)) {
    const item = (projItemList || []).find((i) => i.bidItemId === wp.itemId);
    return item ? item.itemFullCaption : "(Item N/A)";
  }
  if (wpIsCco(wp)) {
    const cco = (projCcoList || []).find((c) => c.ccoId === wp.ccoId);
    return cco ? cco.ccoFullCaption : "(CCO N/A)";
  }
  if (wpIsPendingIssue(wp)) {
    const pi = (projPendingIssueList || []).find(
      (pi) => pi.pendingIssueId === wp.pendingIssueId
    );
    return pi ? pi.pendingIssueFullCaption : "(Pend. Issue N/A)";
  }
  return "";
};

/**
 * Determines whether the provided generalComment object
 * is "blank", for printing purposes.
 *
 * @param {Object} gc
 * @returns {boolean}
 */
export const commentIsBlank = (gc) => {
  if (
    isNullOrEmpty(gc.subject) &&
    isNullOrEmpty(gc.narrative) &&
    gc.keywordIds.length === 0
  )
    return true;
  return false;
};
